import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { theme } from '../styles/GlobalStyles';

const ContactSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background: ${theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing.xxl};
  color: ${theme.colors.text};
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xxl};
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const ContactInfo = styled(motion.div)`
  color: ${theme.colors.textSecondary};
`;

const ContactTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.lg};
  font-weight: 600;
`;

const ContactDescription = styled.p`
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${theme.spacing.xl};
`;

const ContactMethod = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateX(10px);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ContactValue = styled.div`
  color: ${theme.colors.text};
  font-size: 1.1rem;
  font-weight: 500;
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

const SocialLink = styled(motion.a)`
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${theme.colors.text};
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-5px);
    border-color: ${theme.colors.primary};
  }
`;

const ContactForm = styled(motion.form)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: ${theme.spacing.xl};
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const FormLabel = styled.label`
  display: block;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: ${theme.colors.text};
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: ${theme.colors.textSecondary};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: ${theme.colors.text};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: ${theme.colors.textSecondary};
  }
`;

const SubmitButton = styled(motion.button)<{ isSubmitting?: boolean; isSuccess?: boolean }>`
  background: ${props => 
    props.isSuccess 
      ? 'linear-gradient(135deg, #22c55e, #16a34a)' 
      : props.isSubmitting 
        ? 'linear-gradient(135deg, #6b7280, #4b5563)'
        : `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`
  };
  color: white;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: ${props => (props.isSubmitting ? 'not-allowed' : 'pointer')};
  width: 100%;
  transition: all 0.3s ease;
  opacity: ${props => (props.isSubmitting ? 0.7 : 1)};

  &:hover {
    transform: ${props => (props.isSubmitting ? 'none' : 'translateY(-2px)')};
    box-shadow: ${props => (props.isSubmitting ? 'none' : theme.shadows.lg)};
  }
`;

const StatusMessage = styled(motion.div)<{ isError?: boolean }>`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  background: ${props => 
    props.isError 
      ? 'rgba(239, 68, 68, 0.1)' 
      : 'rgba(34, 197, 94, 0.1)'
  };
  color: ${props => (props.isError ? '#ef4444' : '#22c55e')};
  border: 1px solid ${props => (props.isError ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)')};
`;

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Use the API Gateway endpoint from CDK deployment
      const API_URL = 'https://hm7a23awu2.execute-api.us-east-1.amazonaws.com/prod';
      
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Sorry, there was an error sending your message. Please try again or contact me directly at kohnsts@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Get In Touch
        </SectionTitle>

        <ContactContent>
          <ContactInfo
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ContactTitle>Let's Connect</ContactTitle>
            <ContactDescription>
              I'm always interested in hearing about new opportunities, 
              interesting projects, or just want to say hello. Feel free to reach out!
            </ContactDescription>

            <ContactMethod
              whileHover={{ x: 10 }}
              transition={{ duration: 0.3 }}
            >
              <ContactIcon>📧</ContactIcon>
              <ContactDetails>
                <ContactLabel>Email</ContactLabel>
                <ContactValue>simon@simon.mba</ContactValue>
              </ContactDetails>
            </ContactMethod>

            <ContactMethod
              whileHover={{ x: 10 }}
              transition={{ duration: 0.3 }}
            >
              <ContactIcon>📍</ContactIcon>
              <ContactDetails>
                <ContactLabel>Location</ContactLabel>
                <ContactValue>San Francisco, CA</ContactValue>
              </ContactDetails>
            </ContactMethod>

            <ContactMethod
              whileHover={{ x: 10 }}
              transition={{ duration: 0.3 }}
            >
              <ContactIcon>💼</ContactIcon>
              <ContactDetails>
                <ContactLabel>Availability</ContactLabel>
                <ContactValue>Open to new opportunities</ContactValue>
              </ContactDetails>
            </ContactMethod>

            <SocialLinks
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <SocialLink
                href="https://github.com/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                📱
              </SocialLink>
              <SocialLink
                href="https://linkedin.com/in/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                💼
              </SocialLink>
              <SocialLink
                href="https://twitter.com/smkohnstamm"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                🐦
              </SocialLink>
            </SocialLinks>
          </ContactInfo>

          <ContactForm
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            <FormGroup>
              <FormLabel>Name</FormLabel>
              <FormInput 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your name" 
                required 
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Email</FormLabel>
              <FormInput 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com" 
                required 
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Subject</FormLabel>
              <FormInput 
                type="text" 
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="What's this about?" 
                required 
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Message</FormLabel>
              <FormTextarea 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell me more about your project or opportunity..." 
                required 
              />
            </FormGroup>

            <SubmitButton
              type="submit"
              isSubmitting={isSubmitting}
              isSuccess={isSuccess}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : isSuccess ? 'Message Sent!' : 'Send Message'}
            </SubmitButton>

            {(isSuccess || error) && (
              <StatusMessage
                isError={!!error}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {isSuccess ? 'Thank you! Your message has been sent successfully. I\'ll get back to you soon!' : error}
              </StatusMessage>
            )}
          </ContactForm>
        </ContactContent>
      </Container>
    </ContactSection>
  );
};

export default Contact;
